import { stateLabel } from './display-copy';
import { useEffect, useState } from 'react';
import type { OemWorkspace } from '../../lib/oem-client';
import { dailyTasks, deadlineText, taskCategories } from './task-dashboard-model';
function text(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.trim() ? value : fallback;
}
export function TaskDashboard({
  workspace,
  link,
  now,
}: {
  workspace: OemWorkspace;
  link: (key: string) => string;
  now?: number;
}) {
  const [clock, setClock] = useState(() => Date.now());
  const [sku, setSku] = useState('');
  const [category, setCategory] = useState('all');
  useEffect(() => {
    if (now !== undefined) return;
    const refresh = () => setClock(Date.now());
    const visible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    const timer = setInterval(refresh, 60000);
    document.addEventListener('visibilitychange', visible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [now]);
  const tasks = dailyTasks(workspace.records, now ?? clock).filter(
    (task) => !sku || (sku === 'project' ? task.record.skuId === null : task.record.skuId === sku),
  );
  const selected = tasks.filter((task) => category === 'all' || task.category === category);
  const reset = () => {
    setSku('');
    setCategory('all');
  };
  return (
    <section className="card task-dashboard" aria-label="กำหนดส่งงาน">
      <div className="between">
        <h2>กำหนดส่งงาน</h2>
        <a className="text-button" href={link('tasks')}>
          จัดการงาน
        </a>
      </div>
      <p className="muted">
        เวอร์ชันล่าสุดที่มองเห็นของแต่ละงาน · วันอย่างเดียวหมายถึงสิ้นวันในกรุงเทพฯ
      </p>
      <dl className="task-counts">
        {taskCategories.map((bucket) => (
          <div key={bucket.key}>
            <dt>{bucket.label}</dt>
            <dd>{tasks.filter((task) => task.category === bucket.key).length}</dd>
          </div>
        ))}
      </dl>
      <div className="daily-filters">
        <label>
          สินค้าในงาน
          <select
            aria-label="สินค้าในงาน"
            value={sku}
            onChange={(event) => setSku(event.target.value)}
          >
            <option value="">ทุก SKU</option>
            <option value="project">งานรวมของโครงการ</option>
            {workspace.skus.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          กลุ่มกำหนดวัน
          <select
            aria-label="กลุ่มกำหนดวัน"
            value={category}
            onChange={(event) => setCategory(event.target.value)}
          >
            <option value="all">ทุกกลุ่ม</option>
            {taskCategories.map((bucket) => (
              <option key={bucket.key} value={bucket.key}>
                {bucket.label}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="text-button" onClick={reset}>
          ล้างตัวกรองงาน
        </button>
      </div>
      <p role="status">พบ {selected.length} งานในตัวกรองนี้</p>
      {selected.length === 0 ? (
        <div className="daily-empty">
          <p>
            {tasks.length === 0
              ? 'ยังไม่พบงานในขอบเขตหรือตัวกรอง SKU นี้'
              : 'ไม่มีงานในกลุ่มกำหนดวันที่เลือก'}
          </p>
          <button className="action secondary" type="button" onClick={reset}>
            แสดงทุกงานที่มองเห็น
          </button>
        </div>
      ) : (
        <ul
          className="daily-task-list"
          // A labelled overflow list needs native keyboard scrolling while retaining list semantics.
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
          aria-label="รายการงานตามกำหนดวัน"
        >
          {selected.map(({ record, deadline, category: bucket }) => (
            <li key={record.id}>
              <div className="between">
                <strong>{text(record.content.label, 'งานที่ยังไม่มีชื่อ')}</strong>
                <span className={`chip ${bucket === 'overdue' ? 'amber' : ''}`}>
                  {taskCategories.find((item) => item.key === bucket)?.label}
                </span>
              </div>
              <p>{text(record.content.nextAction, 'ยังไม่ระบุขั้นตอนถัดไป')}</p>
              <p className="muted">{deadlineText(deadline)}</p>
              <small>
                v{record.version} · สถานะ: {stateLabel(record.state)} ·{' '}
                {workspace.skus.find((s) => s.id === record.skuId)?.label ?? 'งานรวมของโครงการ'}
              </small>
            </li>
          ))}
        </ul>
      )}
      <p className="scope">กำหนดวันใช้จัดลำดับงาน · ไม่ได้ระบุว่างานเสร็จแล้ว</p>
    </section>
  );
}
